/**
 * Burn-in protection — DVD-style floating clock overlay after idle.
 *
 * Adjust these constants to taste (TV kiosk friendly defaults).
 */
// ── Tunable settings ───────────────────────────────────────
/** Idle time before overlay appears (ms). Default: 5 minutes. */
const IDLE_MS = 5 * 60 * 1000;

/**
 * Bounce speed in CSS pixels per second (overall path speed).
 * The chip moves diagonally and covers the full viewport.
 */
const BOUNCE_SPEED_PX_PER_SEC = 70;

/** Clock text size (CSS). Large TVs: 2.5–3.5rem works well. */
const TEXT_SIZE = 'clamp(1.75rem, 3.2vw, 3.25rem)';

/** Text color (readable on both dark calendar and light widgets). */
const TEXT_COLOR = 'rgba(248, 250, 252, 0.92)';

/** Soft multi-layer shadow for contrast on busy backgrounds. */
const TEXT_SHADOW =
  '0 2px 4px rgba(0,0,0,0.85), 0 0 24px rgba(0,0,0,0.55), 0 0 2px rgba(0,0,0,1)';

/** Overlay scrim over the whole screen (0 = fully clear, 1 = opaque). */
const OVERLAY_SCRIM_OPACITY = 0.18;

/** Clock chip background (behind the text). */
const CHIP_BG = 'rgba(15, 23, 42, 0.42)';

/** Randomize bounce angle slightly on each wall hit (0–0.25). */
const EDGE_JITTER = 0.12;

/** Events that count as user activity. */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'pointerup',
  'mousedown',
  'mousemove',
  'mouseup',
  'click',
  'wheel',
  'keydown',
  'keyup',
  'touchstart',
  'touchmove',
  'touchend',
] as const;
// ── End tunable settings ───────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';

function formatDateTime(d: Date): string {
  // Example: “Thursday, July 30, 2026 – 5:07:32 PM”
  const datePart = d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${datePart} – ${timePart}`;
}

/** Unit diagonal velocity at constant speed (covers full screen over time). */
function randomDiagonalVelocity(speed: number): { vx: number; vy: number } {
  // Angle anywhere in 0..2π except near-axis (avoids pure side-to-side / up-down)
  let angle = Math.random() * Math.PI * 2;
  const axis = Math.min(
    Math.abs(Math.cos(angle)),
    Math.abs(Math.sin(angle))
  );
  if (axis < 0.35) {
    // Push toward 45° diagonals
    const quad = Math.floor(angle / (Math.PI / 2));
    angle = quad * (Math.PI / 2) + (Math.PI / 4) * (0.7 + Math.random() * 0.6);
  }
  return {
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
  };
}

/**
 * After IDLE_MS with no input, show a semi-transparent bouncing clock.
 * pointer-events: none so the live dashboard stays usable underneath.
 * Any activity hides the overlay and resets the idle timer.
 */
export function BurnInProtection() {
  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState(() => formatDateTime(new Date()));

  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clockTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const raf = useRef<number>(0);
  const chipRef = useRef<HTMLDivElement | null>(null);

  // Mutable motion — never driven by React style (avoids reset on re-render)
  const motion = useRef({
    x: 40,
    y: 40,
    vx: 50,
    vy: 50,
    last: 0,
    cw: 320,
    ch: 56,
  });

  const clearIdle = useCallback(() => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  }, []);

  const armIdle = useCallback(() => {
    clearIdle();
    idleTimer.current = setTimeout(() => {
      const m = motion.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Start somewhere on-screen with a true diagonal velocity
      const v = randomDiagonalVelocity(BOUNCE_SPEED_PX_PER_SEC);
      m.vx = v.vx;
      m.vy = v.vy;
      m.x = Math.random() * Math.max(1, vw * 0.5);
      m.y = Math.random() * Math.max(1, vh * 0.5);
      m.last = 0;
      setLabel(formatDateTime(new Date()));
      setVisible(true);
    }, IDLE_MS);
  }, [clearIdle]);

  const onActivity = useCallback(() => {
    setVisible((v) => {
      if (v) return false;
      return v;
    });
    armIdle();
  }, [armIdle]);

  // Idle + activity listeners (stable — no dependency on `visible`)
  useEffect(() => {
    armIdle();
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, opts);
    }
    return () => {
      clearIdle();
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity, opts);
      }
    };
  }, [armIdle, onActivity, clearIdle]);

  // Live clock while visible (label updates must NOT reset position)
  useEffect(() => {
    if (!visible) {
      if (clockTimer.current) {
        clearInterval(clockTimer.current);
        clockTimer.current = null;
      }
      return;
    }
    setLabel(formatDateTime(new Date()));
    clockTimer.current = setInterval(() => {
      setLabel(formatDateTime(new Date()));
    }, 1000);
    return () => {
      if (clockTimer.current) {
        clearInterval(clockTimer.current);
        clockTimer.current = null;
      }
    };
  }, [visible]);

  // Measure chip size when label changes (width can shift slightly)
  useEffect(() => {
    if (!visible) return;
    const chip = chipRef.current;
    if (!chip) return;
    // offset* ignores transform and is stable for layout size
    motion.current.cw = chip.offsetWidth || 320;
    motion.current.ch = chip.offsetHeight || 56;
  }, [visible, label]);

  // Full-screen bounce via left/top (not React-controlled each frame)
  useEffect(() => {
    if (!visible) {
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = 0;
      return;
    }

    const chip = chipRef.current;
    if (chip) {
      motion.current.cw = chip.offsetWidth || 320;
      motion.current.ch = chip.offsetHeight || 56;
      // Apply starting position immediately
      chip.style.left = `${motion.current.x}px`;
      chip.style.top = `${motion.current.y}px`;
    }

    const tick = (t: number) => {
      const m = motion.current;
      const el = chipRef.current;
      if (!el) {
        raf.current = requestAnimationFrame(tick);
        return;
      }

      if (!m.last) m.last = t;
      let dt = (t - m.last) / 1000;
      m.last = t;
      // Clamp huge pauses (tab backgrounded)
      if (dt > 0.05) dt = 0.05;
      if (dt < 0) dt = 0;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Re-measure occasionally in case font metrics settle
      m.cw = el.offsetWidth || m.cw;
      m.ch = el.offsetHeight || m.ch;

      const maxX = Math.max(0, vw - m.cw);
      const maxY = Math.max(0, vh - m.ch);

      m.x += m.vx * dt;
      m.y += m.vy * dt;

      // Bounce off every edge — reverse that axis, keep the other moving
      if (m.x <= 0) {
        m.x = 0;
        m.vx = Math.abs(m.vx);
        m.vy += m.vy * (Math.random() - 0.5) * EDGE_JITTER;
      } else if (m.x >= maxX) {
        m.x = maxX;
        m.vx = -Math.abs(m.vx);
        m.vy += m.vy * (Math.random() - 0.5) * EDGE_JITTER;
      }

      if (m.y <= 0) {
        m.y = 0;
        m.vy = Math.abs(m.vy);
        m.vx += m.vx * (Math.random() - 0.5) * EDGE_JITTER;
      } else if (m.y >= maxY) {
        m.y = maxY;
        m.vy = -Math.abs(m.vy);
        m.vx += m.vx * (Math.random() - 0.5) * EDGE_JITTER;
      }

      // Enforce constant overall speed + never-near-zero axis (full-screen travel)
      let sp = Math.hypot(m.vx, m.vy);
      if (sp < 1e-3) {
        const v = randomDiagonalVelocity(BOUNCE_SPEED_PX_PER_SEC);
        m.vx = v.vx;
        m.vy = v.vy;
        sp = BOUNCE_SPEED_PX_PER_SEC;
      }
      m.vx = (m.vx / sp) * BOUNCE_SPEED_PX_PER_SEC;
      m.vy = (m.vy / sp) * BOUNCE_SPEED_PX_PER_SEC;

      // If angle is too flat (side-to-side only), nudge toward diagonal
      const ax = Math.abs(m.vx);
      const ay = Math.abs(m.vy);
      if (ay < BOUNCE_SPEED_PX_PER_SEC * 0.28) {
        const sign = m.vy >= 0 ? 1 : -1;
        m.vy = sign * BOUNCE_SPEED_PX_PER_SEC * 0.55;
        sp = Math.hypot(m.vx, m.vy);
        m.vx = (m.vx / sp) * BOUNCE_SPEED_PX_PER_SEC;
        m.vy = (m.vy / sp) * BOUNCE_SPEED_PX_PER_SEC;
      }
      if (ax < BOUNCE_SPEED_PX_PER_SEC * 0.28) {
        const sign = m.vx >= 0 ? 1 : -1;
        m.vx = sign * BOUNCE_SPEED_PX_PER_SEC * 0.55;
        sp = Math.hypot(m.vx, m.vy);
        m.vx = (m.vx / sp) * BOUNCE_SPEED_PX_PER_SEC;
        m.vy = (m.vy / sp) * BOUNCE_SPEED_PX_PER_SEC;
      }

      el.style.left = `${m.x}px`;
      el.style.top = `${m.y}px`;

      raf.current = requestAnimationFrame(tick);
    };

    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = 0;
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        pointerEvents: 'none',
        background: `rgba(2, 6, 23, ${OVERLAY_SCRIM_OPACITY})`,
        overflow: 'hidden',
      }}
    >
      <div
        ref={chipRef}
        style={{
          position: 'absolute',
          // Position driven only by JS left/top — do NOT put transform in React
          left: 0,
          top: 0,
          willChange: 'left, top',
          padding: '0.65rem 1.25rem',
          borderRadius: '9999px',
          background: CHIP_BG,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
          whiteSpace: 'nowrap',
          // Keep chip narrower than the screen so it has room to travel
          maxWidth: 'min(90vw, 42rem)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <time
          dateTime={new Date().toISOString()}
          style={{
            fontSize: TEXT_SIZE,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: TEXT_COLOR,
            textShadow: TEXT_SHADOW,
            fontVariantNumeric: 'tabular-nums',
            fontFamily:
              'system-ui, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
            lineHeight: 1.2,
            userSelect: 'none',
          }}
        >
          {label}
        </time>
      </div>
    </div>
  );
}

export const BURN_IN_CONFIG = {
  IDLE_MS,
  BOUNCE_SPEED_PX_PER_SEC,
  TEXT_SIZE,
  TEXT_COLOR,
  OVERLAY_SCRIM_OPACITY,
} as const;
